import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { ToggleGroupItem } from './toggle-group-item'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

type ToggleGroupItemSpec<T extends string> = {
  value: T
  // Segment content; pass a node (e.g. an icon) for icon-only segments
  label: ReactNode
  // Accessible name; defaults to a string label. Set this when label is a node
  ariaLabel?: string
  // Hover tooltip (needs an ancestor TooltipProvider); set for icon-only segments
  tooltip?: string
}

type BaseToggleGroupProps<T extends string> = {
  items: Array<ToggleGroupItemSpec<T>>
  className?: string
  itemClassName?: string
  containerClassName?: string
}

type SingleToggleGroupProps<T extends string> = BaseToggleGroupProps<T> & {
  type: 'single'
  value: T
  onValueChange: (value: T | undefined) => void
}

type MultipleToggleGroupProps<T extends string> = BaseToggleGroupProps<T> & {
  type: 'multiple'
  value: T[]
  onValueChange: (value: T[]) => void
}

type ToggleGroupProps<T extends string> = SingleToggleGroupProps<T> | MultipleToggleGroupProps<T>

export function ToggleGroup<T extends string>(props: ToggleGroupProps<T>) {
  const { className, items, itemClassName, containerClassName, ...rootProps } = props

  return (
    <div className={cn('flex flex-col gap-2', containerClassName)}>
      <ToggleGroupPrimitive.Root
        className={cn('inline-flex rounded border border-gray-4', className)}
        {...(rootProps as any)} // Unfortunately needed due to Radix types
      >
        {items.map((item, index) => {
          const toggleItem = (
            <ToggleGroupItem
              key={item.value}
              aria-label={
                item.ariaLabel ?? (typeof item.label === 'string' ? item.label : undefined)
              }
              className={cn(
                itemClassName,
                index === 0 && 'rounded-l-[4px]',
                index === items.length - 1 && 'rounded-r-[4px]',
                index !== 0 && 'border-s border-gray-4',
              )}
              value={item.value}
            >
              {item.label}
            </ToggleGroupItem>
          )

          if (!item.tooltip) {
            return toggleItem
          }

          return (
            <Tooltip key={item.value}>
              {/* span trigger keeps the Tooltip's data-state off the toggle item, whose own
                  data-[state=on|off] drives its selected/hover styles */}
              <TooltipTrigger asChild>
                <span className="inline-flex">{toggleItem}</span>
              </TooltipTrigger>
              <TooltipContent>{item.tooltip}</TooltipContent>
            </Tooltip>
          )
        })}
      </ToggleGroupPrimitive.Root>
    </div>
  )
}

ToggleGroup.displayName = 'ToggleGroup'
